import { useMemo, useState } from 'react';
import { MagnifyingGlassIcon, PlayCircleIcon } from '@heroicons/react/24/solid';

const NHS_LOGO = '/nhs.png';

const SECTIONS = [
  {
    id: 'nhs-mind-and-mood',
    title: 'NHS Mind and Mood',
    nhs: true,
    videos: [
      { title: 'Stress Relief', url: 'https://www.youtube.com/watch?v=IYTZwBzjNC0' },
      { title: 'Anxiety', url: 'https://www.youtube.com/watch?v=3cXGt2d1RyQ' },
      { title: 'Depression', url: 'https://www.youtube.com/watch?v=qKcRUOWYQ9w' },
      { title: 'Panic Attacks', url: 'https://www.youtube.com/watch?v=SRII9IQ5HOg' },
      { title: 'Mindfulness', url: 'https://www.youtube.com/watch?v=yWuPJFAx3rA' },
      { title: 'Unhelpful Thoughts', url: 'https://www.youtube.com/watch?v=63zXqalSSFk' },
      { title: 'Low Confidence and Assertiveness', url: 'https://www.youtube.com/watch?v=wN4u4tVEDZg' },
      { title: 'Treatment for Anxiety (NHS)', url: 'https://www.youtube.com/watch?v=jLgv0E1k6hs' },
      { title: 'Winter Wellbeing', url: 'https://www.youtube.com/watch?v=khEAkKt5-ho' },
    ],
  },
  {
    id: 'nhs-physical-comfort',
    title: 'NHS Physical Comfort',
    nhs: true,
    videos: [
      { title: 'Desk Ergonomics', url: 'https://www.youtube.com/watch?v=riD8Xt8r1MQ' },
      { title: 'Migraine', url: 'https://www.youtube.com/watch?v=-HkkQoxmXkk' },
      { title: 'Headache', url: 'https://www.youtube.com/watch?v=hvlmqM7eIDM' },
      { title: 'Neck Problems 1', url: 'https://www.youtube.com/watch?v=eme0i13WgTk&list=PLFZZF0a0RYPamotnDWTCVtR17yT_0yrkf' },
      { title: 'Neck Problems 2', url: 'https://www.youtube.com/watch?v=DT1vnU3n6VQ&list=PLFZZF0a0RYPamotnDWTCVtR17yT_0yrkf&index=2' },
      { title: 'Neck Problems 3', url: 'https://www.youtube.com/watch?v=U8MQ_NHhd38&list=PLFZZF0a0RYPamotnDWTCVtR17yT_0yrkf&index=3' },
      { title: 'Neck Problems 4', url: 'https://www.youtube.com/watch?v=rnF0Nw5ZbY4&list=PLFZZF0a0RYPamotnDWTCVtR17yT_0yrkf&index=4' },
      { title: 'Wrist Exercises', url: 'https://www.youtube.com/watch?v=DK7a-HteXrA' },
      { title: 'Wrist Mobility', url: 'https://www.youtube.com/watch?v=gQdSUC5E6GA' },
      { title: 'Back Pain', url: 'https://www.youtube.com/watch?v=VDf43fOTH1E' },
      { title: 'Sore Throat', url: 'https://www.youtube.com/watch?v=pvfDLIsrkjo' },
      { title: 'Treat a Cold', url: 'https://www.youtube.com/watch?v=DyBlpmXEsp8' },
    ],
  },
  {
    id: 'workplace-basics',
    title: 'Workplace Basics',
    nhs: false,
    videos: [
      { title: 'Manual Lifting', url: 'https://www.youtube.com/watch?v=z4epeIusue0' },
      { title: 'Desk Setup', url: 'https://www.youtube.com/watch?v=F8_ME4VwTiw' },
    ],
  },
];

function getYouTubeThumbnail(url) {
  const videoIdMatch = url.match(/[?&]v=([^&]+)/) || url.match(/youtu\.be\/([^?&]+)/);
  const videoId = videoIdMatch ? videoIdMatch[1] : '';
  return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : '';
}

export default function Wellbeing() {
  const [query, setQuery] = useState('');
  const queryLower = query.trim().toLowerCase();

  const filteredSections = useMemo(() => {
    return SECTIONS.map((section) => ({
      ...section,
      videos: section.videos.filter((video) => {
        if (!queryLower) return true;
        return video.title.toLowerCase().includes(queryLower) || video.url.toLowerCase().includes(queryLower);
      }),
    })).filter((section) => section.videos.length > 0);
  }, [queryLower]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-100 via-cyan-100 to-amber-100 p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="rounded-3xl bg-gradient-to-r from-cyan-600 via-emerald-600 to-lime-600 p-6 text-white shadow-xl">
          <h1 className="text-3xl font-black tracking-tight sm:text-4xl">Wellbeing Library</h1>
          <p className="mt-2 max-w-3xl text-sm text-cyan-50 sm:text-base">
            Browse trusted videos for mental wellbeing, physical comfort, and practical workplace support.
          </p>

          <label className="mt-5 flex items-center gap-3 rounded-xl bg-white/20 p-3 backdrop-blur">
            <MagnifyingGlassIcon className="h-5 w-5 text-white" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search videos, topics, or keywords"
              className="w-full bg-transparent text-white placeholder-cyan-100 outline-none"
            />
          </label>
        </div>

        {filteredSections.length === 0 && (
          <div className="rounded-2xl bg-white p-8 text-center text-gray-600 shadow">
            No videos matched your search. Try another keyword.
          </div>
        )}

        {filteredSections.map((section) => (
          <section key={section.id} className="rounded-3xl bg-white/90 p-5 shadow-lg ring-1 ring-white/70 sm:p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-xl font-extrabold text-gray-900 sm:text-2xl">{section.title}</h2>
              {section.nhs && (
                <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-800 ring-1 ring-blue-200">
                  <img src={NHS_LOGO} alt="NHS" className="h-4 w-auto" />
                  NHS Guidance
                </div>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {section.videos.map((video) => (
                <a
                  key={video.url}
                  href={video.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group overflow-hidden rounded-2xl bg-gradient-to-br from-white to-emerald-50 shadow transition hover:-translate-y-0.5 hover:shadow-xl"
                >
                  <div className="relative">
                    <img
                      src={getYouTubeThumbnail(video.url)}
                      alt={video.title}
                      className="h-40 w-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/20 transition group-hover:bg-black/10" />
                    <PlayCircleIcon className="absolute bottom-3 right-3 h-10 w-10 text-white drop-shadow" />
                  </div>
                  <div className="p-4">
                    <p className="text-sm font-bold text-gray-900 sm:text-base">{video.title}</p>
                    <p className="mt-1 text-xs text-gray-500">Watch on YouTube</p>
                  </div>
                </a>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
